/**
 * Configure the API client with the current user's access token.
 * This must be called before making any authenticated API requests.
 *
 * @returns The access token if found, null otherwise
 */
export async function setupAuthenticatedClient(): Promise<string | null> {
  const token = localStorage.getItem("accessToken");

  if (token) {
    // Import apiClient dynamically to avoid circular dependency
    // IMPORTANT: Wait for the import to complete before returning
    const { apiClient } = await import("./api-client");
    apiClient.setSecurityData(token);
    return token;
  }

  return null;
}

/**
 * Store authentication tokens in localStorage and configure the API client
 *
 * @param accessToken - JWT access token
 * @param refreshToken - Refresh token for token renewal
 */
export function storeAuthTokens(
  accessToken: string,
  refreshToken: string
): void {
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  // Import apiClient dynamically to avoid circular dependency
  import("./api-client").then(({ apiClient }) => {
    apiClient.setSecurityData(accessToken);
  });
}

/**
 * Clear authentication tokens and reset the API client
 */
export function clearAuthTokens(): void {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  // Import apiClient dynamically to avoid circular dependency
  import("./api-client").then(({ apiClient }) => {
    apiClient.setSecurityData(null);
  });
}

/**
 * Get the stored access token without configuring the client
 *
 * @returns The access token if found, null otherwise
 */
export function getAccessToken(): string | null {
  return localStorage.getItem("accessToken");
}

/**
 * Get the stored refresh token
 *
 * @returns The refresh token if found, null otherwise
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

/**
 * Check if the user is authenticated (has a valid token)
 * Note: This only checks if a token exists, not if it's valid
 *
 * @returns true if an access token exists
 */
export function isAuthenticated(): boolean {
  return !!localStorage.getItem("accessToken");
}

